import React, { useCallback, useEffect, useRef, useState } from 'react';
import pdsService from '../services/pdsService';
import PdsFormView from './PdsFormView';
import PdsSharePointModal from './PdsSharePointModal';
import { exportPdsFormToPdf } from './pdsPdfExport';

/**
 * Advisor surface for the Personal Data Sheet.
 *
 * Deliberately simpler than the FPR and IM admins: a PDS goes to a brand-new
 * prospect, so there is no Tamarac lookup, no advisor assignment and no
 * meeting scheduling — just name, email, a magic link and progress tracking.
 */

// Must match `reminder_interval_days` on the pds FormConfig in
// backend/service_client_forms/config.py, or the countdown below lies.
const REMINDER_INTERVAL_DAYS = 4;

// Must match len(PDS_DATA_SECTIONS) in backend/service_client_forms/forms/pds.py.
const DEFAULT_TOTAL_SECTIONS = 6;

function createEmptyFormData() {
  return {
    name: '',
    email: '',
    phone: '',
    notes: '',
    client_type: 'hff',
  };
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

const SORTABLE = {
  name: (c) => (c.name || '').toLowerCase(),
  email: (c) => (c.email || '').toLowerCase(),
  status: (c) => (c.completed ? Number.MAX_SAFE_INTEGER : (c.sections_completed || 0)),
  created: (c) => new Date(c.created_at || 0).getTime(),
};

const PdsAdmin = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState(createEmptyFormData());

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [magicLink, setMagicLink] = useState('');
  const [linkClient, setLinkClient] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showProcessed, setShowProcessed] = useState(false);
  const [sortKey, setSortKey] = useState('created');
  const [sortDir, setSortDir] = useState('desc');

  const [detailClient, setDetailClient] = useState(null);
  const [detailForm, setDetailForm] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [exportingId, setExportingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [sharePointClient, setSharePointClient] = useState(null);

  // Guard against a slow response painting under a different client after
  // rapid row-clicking or tab-switching.
  const listAbortRef = useRef(null);
  const detailAbortRef = useRef(null);
  const detailSeqRef = useRef(0);

  const fetchClients = useCallback(async () => {
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    try {
      setLoading(true);
      setError('');
      const data = await pdsService.fetchAllClients({ signal: controller.signal });
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  useEffect(() => () => {
    detailSeqRef.current += 1;
    listAbortRef.current?.abort();
    detailAbortRef.current?.abort();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError('');
      const result = await pdsService.createClient({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        notes: formData.notes || null,
        client_type: formData.client_type || 'hff',
        // Nothing is emailed from here — PDS invites go out through Power
        // Automate. This mints the record and hands back the link to send.
        send_mode: 'none',
      });
      setSuccess(
        `Prospect created. Magic link: ${result.magic_link} — reminders are `
        + 'paused until you resume them, so they only start after you send it.',
      );
      setShowCreateModal(false);
      setFormData(createEmptyFormData());
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;
    try {
      setSubmitting(true);
      setError('');
      await pdsService.deleteClient(selectedClient.client_id);
      setSuccess(`Deleted prospect: ${selectedClient.name}`);
      setShowDeleteModal(false);
      setSelectedClient(null);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGetLink = async (client) => {
    setLinkClient(client);
    setMagicLink('');
    setShowLinkModal(true);
    try {
      setLinkLoading(true);
      setError('');
      const result = await pdsService.getLink(client.client_id);
      setMagicLink(result.magic_link);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setShowLinkModal(false);
    } finally {
      setLinkLoading(false);
    }
  };

  const handleTogglePause = async (client) => {
    try {
      setError('');
      const pausing = !client.reminders_paused;
      await pdsService.patchClient(client.client_id, { reminders_paused: pausing });
      setSuccess(`Reminders ${pausing ? 'paused' : 'resumed'} for ${client.name}.`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleToggleProcessed = async (client) => {
    try {
      setError('');
      const nextProcessed = !client.processed;
      await pdsService.patchClient(client.client_id, { processed: nextProcessed });
      setSuccess(`${nextProcessed ? 'Marked' : 'Unmarked'} as processed: ${client.name}`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleExport = async (client) => {
    try {
      setError('');
      setExportingId(client.client_id);
      const data = await pdsService.getClientDetail(client.client_id);
      exportPdsFormToPdf(data?.form?.form_data || {}, {
        clientName: client.name,
        clientEmail: client.email,
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'PDF export failed');
    } finally {
      setExportingId(null);
    }
  };

  // Client Services chasing a prospect who has not finished the form since
  // Ann-Marie's initial email. This rotates the token, so any link already
  // sent stops working — that is why it confirms first.
  const handleResend = async (client) => {
    const proceed = window.confirm(
      `Email ${client.name} a reminder with a fresh link?\n\n`
      + 'Their previous link will stop working.',
    );
    if (!proceed) return;

    try {
      setError('');
      setResendingId(client.client_id);
      await pdsService.resendLink(client.client_id);
      setSuccess(`Reminder sent to ${client.email}.`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Resend failed');
    } finally {
      setResendingId(null);
    }
  };

  const copyToClipboard = (text, clientId) => {
    navigator.clipboard.writeText(text);
    setCopiedId(clientId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleViewClient = async (client) => {
    if (detailClient?.client_id === client.client_id) {
      setDetailClient(null);
      setDetailForm(null);
      return;
    }

    detailAbortRef.current?.abort();
    const controller = new AbortController();
    detailAbortRef.current = controller;
    const seq = ++detailSeqRef.current;

    try {
      setDetailLoading(true);
      setDetailClient(client);
      setDetailForm(null);
      const data = await pdsService.getClientDetail(client.client_id, {
        signal: controller.signal,
      });
      if (seq !== detailSeqRef.current) return;
      setDetailForm(data.form?.form_data || {});
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      setError(err.response?.data?.detail || err.message);
      setDetailClient(null);
    } finally {
      if (seq === detailSeqRef.current) setDetailLoading(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return 'bi-chevron-expand';
    return sortDir === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down';
  };

  const filteredClients = clients.filter((c) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term
      || c.name?.toLowerCase().includes(term)
      || c.email?.toLowerCase().includes(term);
    if (!matchesSearch) return false;
    if (!showProcessed && c.processed) return false;

    const isComplete = !!c.completed;
    return statusFilter === 'all'
      || (statusFilter === 'completed' && isComplete)
      || (statusFilter === 'in_progress' && !isComplete && (c.sections_completed || 0) > 0)
      || (statusFilter === 'not_started' && !isComplete && (c.sections_completed || 0) === 0);
  });

  const sortedClients = [...filteredClients].sort((a, b) => {
    const get = SORTABLE[sortKey] || SORTABLE.created;
    const av = get(a);
    const bv = get(b);
    if (av === bv) return 0;
    return (av > bv ? 1 : -1) * (sortDir === 'asc' ? 1 : -1);
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-muted small">
          <i className="bi bi-info-circle me-1" />
          Send the Personal Data Sheet to a new prospect ahead of their Getting
          Started meeting, and track whether they have started it.
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <i className="bi bi-person-plus me-1" /> Add Prospect
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} />
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <div style={{ wordBreak: 'break-all' }}>{success}</div>
          {success.includes('http') && (
            <button
              className="btn btn-sm btn-outline-success mt-2"
              onClick={() => {
                const url = success.match(/(https?:\/\/\S+)/)?.[1];
                if (url) copyToClipboard(url, 'success');
              }}
            >
              <i className="bi bi-clipboard me-1" />
              {copiedId === 'success' ? 'Copied!' : 'Copy Link'}
            </button>
          )}
          <button type="button" className="btn-close" onClick={() => setSuccess('')} />
        </div>
      )}

      <div className="mb-3 d-flex gap-2 flex-wrap align-items-center">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: '320px' }}
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="btn-group">
          {[
            { key: 'all', label: 'All' },
            { key: 'completed', label: 'Completed' },
            { key: 'in_progress', label: 'In Progress' },
            { key: 'not_started', label: 'Not Started' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ${statusFilter === key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`btn btn-sm ${showProcessed ? 'btn-warning' : 'btn-outline-secondary'}`}
          onClick={() => setShowProcessed((v) => !v)}
          title={showProcessed ? 'Showing processed prospects' : 'Hiding processed prospects'}
        >
          <i className={`bi ${showProcessed ? 'bi-eye' : 'bi-eye-slash'} me-1`} />
          {showProcessed ? 'Hide Processed' : 'Show Processed'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : sortedClients.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox display-4 d-block mb-2" />
          {searchTerm || statusFilter !== 'all' || !showProcessed
            ? 'No prospects match your filters.'
            : 'No prospects yet. Click "Add Prospect" to send the first Personal Data Sheet.'}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                {[
                  { key: 'name', label: 'Name' },
                  { key: 'email', label: 'Email' },
                  { key: 'status', label: 'Status' },
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    role="button"
                    onClick={() => handleSort(key)}
                    style={{ cursor: 'pointer' }}
                  >
                    {label} <i className={`bi ${sortIcon(key)} small`} />
                  </th>
                ))}
                <th>Reminders</th>
                <th>Device</th>
                <th
                  role="button"
                  onClick={() => handleSort('created')}
                  style={{ cursor: 'pointer' }}
                >
                  Created <i className={`bi ${sortIcon('created')} small`} />
                </th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedClients.map((client) => {
                const filled = client.sections_completed || 0;
                const totalSections = client.total_sections || DEFAULT_TOTAL_SECTIONS;
                // Submitted, not "clicked through every tab" — a form that
                // was never submitted has not reached Client Services.
                const isComplete = !!client.completed;
                const pct = isComplete ? 100 : Math.round((filled / totalSections) * 100);
                const color = isComplete ? 'bg-success' : pct > 0 ? 'bg-info' : 'bg-secondary';

                return (
                  <tr
                    key={client.client_id}
                    onClick={() => handleViewClient(client)}
                    style={{ cursor: 'pointer' }}
                    className={detailClient?.client_id === client.client_id ? 'table-active' : ''}
                  >
                    <td className="fw-semibold">{client.name}</td>
                    <td>{client.email}</td>
                    <td style={{ minWidth: '140px' }}>
                      <div className="progress" style={{ height: '18px' }}>
                        <div
                          className={`progress-bar ${color}`}
                          role="progressbar"
                          style={{ width: `${Math.max(pct, pct > 0 ? 10 : 0)}%` }}
                        >
                          {pct > 0 ? `${pct}%` : ''}
                        </div>
                      </div>
                      <small className="text-muted">
                        {client.processed
                          ? 'Processed'
                          : isComplete
                            ? 'Completed'
                            : pct === 0
                              ? 'Not Started'
                              : `${filled} of ${totalSections} sections`}
                      </small>
                    </td>
                    <td>
                      {(() => {
                        if (client.processed || isComplete) {
                          return <span className="text-muted">—</span>;
                        }
                        if (client.reminders_paused) {
                          return (
                            <span className="badge bg-warning text-dark">
                              <i className="bi bi-pause-circle me-1" />Paused
                            </span>
                          );
                        }
                        const ref = client.last_reminder_sent || client.created_at;
                        if (!ref) return <span className="text-muted">—</span>;
                        const daysSince = Math.floor(
                          (Date.now() - new Date(ref).getTime()) / 86400000,
                        );
                        const daysLeft = REMINDER_INTERVAL_DAYS - daysSince;
                        if (daysLeft <= 0) {
                          return (
                            <span className="badge bg-danger">
                              <i className="bi bi-envelope me-1" />Due now
                            </span>
                          );
                        }
                        return <span className="text-muted small">{daysLeft}d until next</span>;
                      })()}
                    </td>
                    <td>
                      {client.device_type ? (
                        <span title={client.device_type}>
                          <i className={`bi ${client.device_type === 'mobile' ? 'bi-phone' : 'bi-laptop'}`} />
                          {' '}{client.device_type === 'mobile' ? 'Mobile' : 'Desktop'}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{formatDate(client.created_at)}</td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-outline-secondary"
                          title="Get magic link"
                          onClick={() => handleGetLink(client)}
                        >
                          <i className="bi bi-link-45deg" />
                        </button>
                        <button
                          className="btn btn-outline-success"
                          title="Export Personal Data Sheet PDF"
                          onClick={() => handleExport(client)}
                          disabled={exportingId === client.client_id}
                        >
                          {exportingId === client.client_id ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            <i className="bi bi-file-earmark-pdf" />
                          )}
                        </button>
                        <button
                          className="btn btn-outline-info"
                          title="Upload Personal Data Sheet PDF to SharePoint"
                          onClick={() => setSharePointClient(client)}
                        >
                          <i className="bi bi-cloud-upload" />
                        </button>
                        <button
                          className="btn btn-outline-primary"
                          title="Email a reminder with a fresh link"
                          onClick={() => handleResend(client)}
                          disabled={resendingId === client.client_id}
                        >
                          {resendingId === client.client_id ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            <i className="bi bi-send" />
                          )}
                        </button>
                        <button
                          className={`btn ${client.reminders_paused ? 'btn-warning' : 'btn-outline-secondary'}`}
                          title={client.reminders_paused ? 'Resume reminder emails' : 'Pause reminder emails'}
                          onClick={() => handleTogglePause(client)}
                        >
                          <i className={`bi ${client.reminders_paused ? 'bi-play-circle' : 'bi-pause-circle'} me-1`} />
                          {client.reminders_paused ? 'Resume' : 'Pause'}
                        </button>
                        <button
                          className={`btn ${client.processed ? 'btn-warning' : 'btn-outline-warning'}`}
                          title={client.processed ? 'Mark as unprocessed' : 'Mark as processed'}
                          onClick={() => handleToggleProcessed(client)}
                        >
                          <i className={`bi ${client.processed ? 'bi-arrow-counterclockwise' : 'bi-check2-circle'}`} />
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          title="Delete prospect"
                          onClick={() => { setSelectedClient(client); setShowDeleteModal(true); }}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Client Detail Panel */}
      {detailClient && (
        <div className="card mt-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-person-lines-fill me-2" />
              {detailClient.name}
              <small className="text-muted ms-2">({detailClient.email})</small>
            </h5>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-outline-success"
                title="Export Personal Data Sheet PDF"
                onClick={() => handleExport(detailClient)}
                disabled={exportingId === detailClient.client_id}
              >
                {exportingId === detailClient.client_id ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <i className="bi bi-file-earmark-pdf me-1" />
                    Export PDF
                  </>
                )}
              </button>
              <button
                className="btn btn-sm btn-outline-info"
                title="Upload Personal Data Sheet PDF to SharePoint"
                onClick={() => setSharePointClient(detailClient)}
              >
                <i className="bi bi-cloud-upload me-1" />
                Upload to SharePoint
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                title="Email a reminder with a fresh link"
                onClick={() => handleResend(detailClient)}
                disabled={resendingId === detailClient.client_id}
              >
                {resendingId === detailClient.client_id ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <i className="bi bi-send me-1" />
                    Resend
                  </>
                )}
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => { setDetailClient(null); setDetailForm(null); }}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          </div>
          <div className="card-body">
            {detailLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <PdsFormView formData={detailForm} />
            )}
          </div>
        </div>
      )}

      {/* Create Prospect Modal */}
      {showCreateModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleCreate}>
                <div className="modal-header">
                  <h5 className="modal-title">Send a Personal Data Sheet</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCreateModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <p className="text-muted small">
                    For a prospect Power Automate hasn't already created. Enter
                    their name and email and you'll get a personal link to send
                    them — nothing is emailed from here. They can return to the
                    link as many times as they need.
                  </p>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="pds-name">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      id="pds-name"
                      type="text"
                      className="form-control"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="pds-email">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      id="pds-email"
                      type="email"
                      className="form-control"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                    <div className="form-text">
                      The form is needed 5 business days before the Getting
                      Started meeting.
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="pds-phone">Phone</label>
                    <input
                      id="pds-phone"
                      type="text"
                      className="form-control"
                      placeholder="+1-555-555-0123"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="pds-notes">Notes</label>
                    <textarea
                      id="pds-notes"
                      className="form-control"
                      rows={2}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="pds-theme">Client Theme</label>
                    <select
                      id="pds-theme"
                      className="form-select"
                      value={formData.client_type}
                      onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                    >
                      <option value="hff">HFF</option>
                      <option value="rebellionaire">Rebellionaire</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Creating…' : 'Create Prospect & Get Link'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedClient && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Prospect</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">
                Are you sure you want to delete <strong>{selectedClient.name}</strong>?
                Their Personal Data Sheet answers will be deleted too. This cannot be undone.
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={submitting}
                >
                  {submitting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Magic Link Modal */}
      {showLinkModal && linkClient && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Magic Link — {linkClient.name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowLinkModal(false)} />
              </div>
              <div className="modal-body">
                {linkLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="input-group">
                      <input type="text" className="form-control" readOnly value={magicLink} />
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => copyToClipboard(magicLink, linkClient.client_id)}
                      >
                        <i className="bi bi-clipboard me-1" />
                        {copiedId === linkClient.client_id ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <div className="form-text mt-2">
                      This link is unique to {linkClient.name} and replaces any
                      link previously issued to them.
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowLinkModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {sharePointClient && (
        <PdsSharePointModal
          client={sharePointClient}
          onClose={() => setSharePointClient(null)}
          onSuccess={() => {
            setSharePointClient(null);
            setSuccess(
              `Personal Data Sheet uploaded to ${sharePointClient.name}'s main SharePoint folder.`,
            );
          }}
        />
      )}

    </div>
  );
};

export default PdsAdmin;
